// (#01.2) หน้าจัดการ/ยกเลิก Label ใน Admin Web
//
// ปัญหาต้นเรื่อง: สั่ง print แล้ว label ไม่ออก พนักงานกด print ซ้ำ เกิด label เกินค้างในระบบ
// หน้านี้ให้ค้นหา label แล้วยกเลิก (void) ตัวที่พิมพ์เกิน/ไม่ได้ใช้ พร้อมบันทึกผู้ยกเลิกและเหตุผล
//
// ยกเลิกได้เฉพาะ label ที่ยังไม่ถูกใช้งาน (backend เป็นคนตัดสินและส่ง can_void มาให้)
import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import AppHeader from "../components/AppHeader.js";
import ScrollToTopButton from "../components/ScrollToTopButton.js";
import {
  GET,
  POST,
  alert,
  loading,
  Select,
  DatePicker,
  Pagination,
  Modal,
  toFixed,
  format_date,
  XLSX,
  saveAs,
} from "../components/CustomComponent.js";
import "../assets/css/bootstrap.css";
import "../assets/css/style.css";
import "../assets/css/manora-report.css";

const PAGE_SIZE = 100;
const IMPORT_STAGES = ["upload", "parse", "validate", "lots", "pallets", "qc", "receive", "labels", "logs", "commit"];
const MAX_IMPORT_ISSUES_SHOWN = 300;
const MIN_REASON_LENGTH = 3;

const cleanParams = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== "" && v !== null && v !== undefined));

class LabelManage extends Component {
  state = {
    filters: {
      data_search: "",
      product_id: "",
      created_warehouse_id: "",
      date_from: "",
      date_to: "",
      // (WEB-260901-14) ค่าเริ่มต้นต้องเป็น "ทุกสถานะ" — ของจริง label เกือบทั้งหมดถูกใช้ไปแล้ว
      // (ผูก qc_id / ผ่านสถานีใดสถานีหนึ่ง) พอ default เป็น "ใช้งานอยู่" ตารางจึงว่างทุกครั้งที่เปิดหน้า
      // ทั้งที่การ์ดสรุปด้านบนบอกว่ามีข้อมูลอยู่
      status: "all",
    },
    page: 1,
    items: [],
    pagination: null,
    // สรุปยอดรวมทุกสถานะ (ไม่ผูกกับ filters.status) กันหน้าดูเหมือน "ไม่มีข้อมูลเลย"
    // ตอน filter สถานะที่เลือกอยู่บังเอิญไม่มีรายการ ทั้งที่จริงมีข้อมูลอยู่ในสถานะอื่น
    summary: { total: 0, active: 0, used: 0, voided: 0 },

    products: [],
    warehouses: [],

    // modal ยกเลิก label
    voidTarget: null,
    voidReason: "",
    voidError: "",
    isVoiding: false,

    // modal นำเข้า (ยกยอด) Lot / พาเลท / Label จากไฟล์ template — ตรวจไฟล์ก่อน แล้วค่อยยืนยันนำเข้า
    importOpen: false,
    importFile: null,
    importResult: null,
    importError: "",
    importJob: null, // ความคืบหน้าจาก backend ระหว่างทำงาน
    importDryRun: true,
    importElapsed: 0,
    isImporting: false,
  };

  async componentDidMount() {
    loading(true);
    const [productsRes, warehousesRes] = await Promise.all([
      GET("/api/v1/internal/product", { page: 0 }),
      GET("/api/v1/internal/warehouse", { page: 0 }),
    ]);
    this.setState({
      products: productsRes?.status === true ? productsRes.data : [],
      warehouses: warehousesRes?.status === true ? warehousesRes.data : [],
    });
    await this.Get(1);
  }

  Get = async (page) => {
    loading(true);
    const params = cleanParams({ ...this.state.filters, page, page_size: PAGE_SIZE });
    const result = await GET("/api/v1/internal/label-management", params);
    if (result?.status === true) {
      this.setState({
        page,
        items: result.data || [],
        pagination: result.pagination,
        summary: result.summary || { total: 0, active: 0, used: 0, voided: 0 },
      });
    }
    loading(false);
  };

  updateFilter = (key, value) => {
    this.setState({ filters: { ...this.state.filters, [key]: value } }, () => this.Get(1));
  };

  // ---------- ยกเลิก label ----------
  openVoidModal = (item) => {
    this.setState({ voidTarget: item, voidReason: "", voidError: "" });
  };

  closeVoidModal = () => {
    if (this.state.isVoiding) return;
    this.setState({ voidTarget: null, voidReason: "", voidError: "" });
  };

  confirmVoid = async () => {
    const { t } = this.props;
    const { voidTarget, voidReason } = this.state;
    const reason = voidReason.trim();

    if (reason.length < MIN_REASON_LENGTH) {
      this.setState({ voidError: t("labels.void_reason_required") });
      return;
    }

    this.setState({ isVoiding: true, voidError: "" });
    const result = await POST(`/api/v1/internal/label-management/void/${voidTarget.uid}`, { reason });
    this.setState({ isVoiding: false });

    if (result?.status === true) {
      this.setState({ voidTarget: null, voidReason: "" });
      await this.Get(this.state.page);
      alert("", "success", t("labels.void_success"), voidTarget.label_no || "");
    } else {
      this.setState({ voidError: result?.message || t("labels.void_failed") });
    }
  };

  // ---------- นำเข้าข้อมูลยกยอด ----------
  openImportModal = () => {
    this.setState({ importOpen: true, importFile: null, importResult: null, importError: "" });
  };

  closeImportModal = () => {
    if (this.state.isImporting) return;
    this.setState({ importOpen: false, importFile: null, importResult: null, importError: "" });
  };

  readFileBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(",").pop());
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // dryRun=true ตรวจไฟล์อย่างเดียว · false นำเข้าจริง (backend ทำทั้งไฟล์ในทรานแซกชันเดียว ผิดแถวเดียว = ไม่นำเข้าเลย)
  // backend รันเป็นงานเบื้องหลัง (ไฟล์ใหญ่ใช้เวลานานเกิน timeout ของ request) — เริ่มงานแล้วถามความคืบหน้าทุก 1 วิ
  submitImport = async (dryRun) => {
    const { t } = this.props;
    const { importFile } = this.state;
    if (!importFile) return;

    this.setState({ importDryRun: dryRun, isImporting: true, importResult: null, importError: "", importJob: { stage: "upload", done: 0, total: 1 } });
    this.importStartedAt = Date.now();
    this.startImportTicker();
    const file_base64 = await this.readFileBase64(importFile);
    const started = await POST(
      "/api/v1/internal/production-import/migration",
      { file_base64, dry_run: dryRun, async: true },
      false
    );
    if (started?.status !== true || !started.data?.job_id) {
      this.finishImport({ importError: started?.message || t("labels.import_failed") });
      return;
    }

    const job = await this.pollImportJob(started.data.job_id);
    if (!job || job.status === "failed") {
      this.finishImport({ importError: job?.error || t("labels.import_failed") });
      return;
    }
    this.finishImport({ importResult: job.result });

    if (!dryRun && job.result?.imported) {
      const imported = job.result.imported;
      await this.Get(1);
      alert(
        "",
        "success",
        t("labels.import_success"),
        t("labels.import_success_text", { pallets: imported.pallets, labels: imported.labels })
      );
      this.setState({ importOpen: false, importFile: null, importResult: null });
    }
  };

  pollImportJob = async (jobId) => {
    let failures = 0;
    for (;;) {
      await new Promise((r) => setTimeout(r, 1000));
      if (this.unmounted) return null;
      const res = await GET(`/api/v1/internal/production-import/migration/jobs/${jobId}`);
      if (res?.status === true && res.data) {
        failures = 0;
        this.setState({ importJob: res.data });
        if (res.data.status !== "running") return res.data;
      } else if (++failures >= 10) {
        // เครือข่ายหลุดติดกัน ~10 วิ — เลิกถาม (งานฝั่ง server ยังทำต่อ/rollback เองตามปกติ)
        return { status: "failed", error: this.props.t("labels.import_lost") };
      }
    }
  };

  // นาฬิกาเวลาที่ผ่านไป เดินทุกวินาทีแม้ขั้นตอนเดียวกันจะใช้เวลานาน ให้เห็นว่าไม่ได้ค้าง
  startImportTicker = () => {
    clearInterval(this.importTicker);
    this.setState({ importElapsed: 0 });
    this.importTicker = setInterval(() => {
      this.setState({ importElapsed: Math.floor((Date.now() - this.importStartedAt) / 1000) });
    }, 1000);
  };

  finishImport = (state) => {
    clearInterval(this.importTicker);
    this.setState({ isImporting: false, importJob: null, ...state });
  };

  componentWillUnmount() {
    this.unmounted = true;
    clearInterval(this.importTicker);
  }

  // % รวมจากลำดับขั้นตอนจริงของ backend (STAGES_ORDER ใน production-import.service.js)
  importProgress = (job, dryRun) => {
    const stages = dryRun ? ["upload", "parse", "validate"] : IMPORT_STAGES;
    const index = Math.max(0, stages.indexOf(job.stage));
    const within = job.total > 0 ? Math.min(1, job.done / job.total) : 0;
    return Math.min(99, Math.round(((index + within) / stages.length) * 100));
  };

  ExportExcel = async () => {
    const { t } = this.props;
    loading(true);
    const params = cleanParams({ ...this.state.filters, page: 0 });
    const result = await GET("/api/v1/internal/label-management", params);
    if (result?.status === true) {
      const rows = (result.data || []).map((item) => ({
        "Label No": item.label_no || "",
        "LOT NO": item.batch_no || "",
        รหัสสินค้า: item.product?.code || "",
        ชื่อสินค้า: item.product?.name || "",
        ถุงที่: item.bag_no ?? "",
        จำนวน: item.amount ?? "",
        หน่วย: item.unit?.code || "",
        วันผลิต: item.production_date ? format_date(item.production_date) : "",
        คลังที่สร้าง: item.created_warehouse?.name || "",
        Pallet: item.pallet?.pallet_no || "",
        ผู้สร้าง: item.created_by_name || "",
        สถานะ: item.is_voided
          ? t("labels.badge_voided")
          : item.is_used
            ? t("labels.badge_used")
            : t("labels.badge_active"),
        ผู้ยกเลิก: item.voided_by_name || "",
        วันที่ยกเลิก: item.voided_at ? format_date(item.voided_at) : "",
        เหตุผลที่ยกเลิก: item.void_reason || "",
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Labels");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([buf], { type: "application/octet-stream" }),
        `labels-${format_date(new Date(), "yyyy-mm-dd")}.xlsx`,
      );
    }
    loading(false);
  };

  renderStatusBadge = (item) => {
    const { t } = this.props;
    if (item.is_voided) {
      return <span className="manora-badge manora-badge-pink">{t("labels.badge_voided")}</span>;
    }
    if (item.is_used) {
      return <span className="manora-badge manora-badge-gray">{t("labels.badge_used")}</span>;
    }
    return <span className="manora-badge manora-badge-green">{t("labels.badge_active")}</span>;
  };

  render() {
    const { filters, items, pagination, summary, products, warehouses, voidTarget, voidReason, voidError, isVoiding } =
      this.state;
    const { importOpen, importFile, importResult, importError, importJob, importDryRun, importElapsed, isImporting } =
      this.state;
    const importIssues = importResult
      ? [
          ...importResult.errors.map((e) => ({ ...e, level: "error" })),
          ...importResult.warnings.map((w) => ({ ...w, level: "warning" })),
        ]
      : [];
    const { t } = this.props;

    // จำนวนที่ตรงกับตัวกรองปัจจุบัน (ใช้กับหัวตาราง/pagination เท่านั้น)
    const filteredCount = pagination?.count ?? items.length;
    // สรุปยอดรวมทุกสถานะ (ไม่ผูกกับ filters.status) — มาจาก API โดยตรง ไม่ใช่นับจาก items ที่ถูกกรองแล้ว
    const total = summary.total;
    const voidableCount = summary.active;
    const usedCount = summary.used;
    const voidedCount = summary.voided;

    return (
      <div className="w-100 min-vh-100" style={{ background: "#f5f3fa" }}>
        <AppHeader title={t("labels.title")} subtitle={t("labels.subtitle")} activeTab="labels" />

        <div className="p-3">
          {/* ---------- ตัวกรอง ---------- */}
          <div className="manora-filter-card d-flex flex-wrap align-items-end mb-3" style={{ gap: 10 }}>
            <div style={{ minWidth: 200 }}>
              <div className="text-12 text-secondary mb-1">{t("labels.filter_search")}</div>
              <input
                className="form-control"
                placeholder={t("labels.filter_search_placeholder")}
                value={filters.data_search}
                onChange={(e) => this.updateFilter("data_search", e.target.value)}
              />
            </div>
            <div style={{ minWidth: 170 }}>
              <div className="text-12 text-secondary mb-1">{t("labels.filter_product")}</div>
              <Select
                className="form-control"
                options={products.map((p) => ({ value: p.id, label: p.alias || p.name }))}
                value={filters.product_id}
                onChange={(v) => this.updateFilter("product_id", v)}
              />
            </div>
            <div style={{ minWidth: 150 }}>
              <div className="text-12 text-secondary mb-1">{t("labels.filter_warehouse")}</div>
              <Select
                className="form-control"
                options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
                value={filters.created_warehouse_id}
                onChange={(v) => this.updateFilter("created_warehouse_id", v)}
              />
            </div>
            <div style={{ minWidth: 140 }}>
              <div className="text-12 text-secondary mb-1">{t("labels.filter_date_from")}</div>
              <DatePicker
                className="form-control"
                value={filters.date_from}
                onChange={(v) => this.updateFilter("date_from", v)}
              />
            </div>
            <div style={{ minWidth: 140 }}>
              <div className="text-12 text-secondary mb-1">{t("labels.filter_date_to")}</div>
              <DatePicker
                className="form-control"
                value={filters.date_to}
                onChange={(v) => this.updateFilter("date_to", v)}
              />
            </div>
            <div style={{ minWidth: 140 }}>
              <div className="text-12 text-secondary mb-1">{t("labels.filter_status")}</div>
              <Select
                className="form-control"
                // (#379) ตัวเลือกต้องครบเท่าที่ตารางแสดง: ใช้งานอยู่ / ใช้แล้ว / ยกเลิก / ทั้งหมด
                options={[
                  { value: "active", label: t("labels.status_active") },
                  { value: "used", label: t("labels.status_used") },
                  { value: "voided", label: t("labels.status_voided") },
                  { value: "all", label: t("labels.status_all") },
                ]}
                value={filters.status}
                onChange={(v) => this.updateFilter("status", v || "all")}
              />
            </div>
            <button className="btn btn-success" onClick={() => this.ExportExcel()}>
              {t("labels.export_excel")}
            </button>
            <button className="btn btn-outline-primary" onClick={this.openImportModal}>
              {t("labels.import_button")}
            </button>
          </div>

          {/* ---------- สรุป ---------- */}
          <div className="row g-2 mb-3">
            <div className="col-6 col-md-3">
              <div className="manora-summary-card border-purple h-100">
                <div className="manora-summary-label">{t("labels.summary_total")}</div>
                <div className="manora-summary-value">
                  {total} {t("labels.summary_unit")}
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="manora-summary-card border-green h-100">
                <div className="manora-summary-label">{t("labels.summary_voidable")}</div>
                <div className="manora-summary-value">
                  {voidableCount} {t("labels.summary_unit")}
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="manora-summary-card border-blue h-100">
                <div className="manora-summary-label">{t("labels.summary_used")}</div>
                <div className="manora-summary-value">
                  {usedCount} {t("labels.summary_unit")}
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="manora-summary-card border-orange h-100">
                <div className="manora-summary-label">{t("labels.summary_voided")}</div>
                <div className="manora-summary-value">
                  {voidedCount} {t("labels.summary_unit")}
                </div>
              </div>
            </div>
          </div>

          {/* ---------- ตาราง ---------- */}
          <div className="manora-card">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="fw-bold">{t("labels.list_title")}</div>
              <small className="text-secondary">
                {filteredCount} {t("labels.summary_unit")}
              </small>
            </div>
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <td className="text-nowrap">{t("labels.col_label_no")}</td>
                    <td className="text-nowrap">{t("labels.col_lot_no")}</td>
                    <td className="text-nowrap">{t("labels.col_product")}</td>
                    <td className="text-nowrap text-end">{t("labels.col_bag_no")}</td>
                    <td className="text-nowrap text-end">{t("labels.col_amount")}</td>
                    <td className="text-nowrap">{t("labels.col_production_date")}</td>
                    <td className="text-nowrap">{t("labels.col_warehouse")}</td>
                    <td className="text-nowrap">{t("labels.col_pallet")}</td>
                    <td className="text-nowrap">{t("labels.col_created_by")}</td>
                    <td className="text-nowrap">{t("labels.col_status")}</td>
                    <td className="text-nowrap text-end">{t("labels.col_action")}</td>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.uid}>
                      <td className="manora-link-text text-nowrap">{item.label_no || "-"}</td>
                      <td className="text-nowrap">{item.batch_no || "-"}</td>
                      <td>
                        <div>{item.product?.name || "-"}</div>
                        <div className="text-12 text-secondary">{item.product?.code || ""}</div>
                      </td>
                      <td className="text-end text-nowrap">{item.bag_no ?? "-"}</td>
                      <td className="text-end text-nowrap">
                        {item.amount != null ? toFixed(item.amount) : "-"} {item.unit?.code || ""}
                      </td>
                      <td className="text-nowrap">
                        {item.production_date ? format_date(item.production_date) : "-"}
                      </td>
                      <td className="text-nowrap">{item.created_warehouse?.name || "-"}</td>
                      <td className="text-nowrap">{item.pallet?.pallet_no || "-"}</td>
                      <td className="text-nowrap">{item.created_by_name || "-"}</td>
                      <td>
                        {this.renderStatusBadge(item)}
                        {item.is_voided && (
                          <div className="text-12 text-secondary mt-1">
                            {t("labels.voided_meta", {
                              name: item.voided_by_name || "-",
                              date: item.voided_at ? format_date(item.voided_at) : "-",
                            })}
                            {item.void_reason && (
                              <div>
                                {t("labels.void_reason_prefix")}: {item.void_reason}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="text-end text-nowrap">
                        {item.can_void ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => this.openVoidModal(item)}
                          >
                            {t("labels.void_button")}
                          </button>
                        ) : (
                          <span className="text-12 text-secondary">
                            {item.is_voided ? "-" : t("labels.cannot_void_used")}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={11} className="text-center text-secondary py-4">
                        {/* มีข้อมูลอยู่แต่ตัวกรองปัจจุบันไม่เจอ -> บอกให้ชัด ไม่ให้เข้าใจผิดว่าระบบไม่มีข้อมูล */}
                        {total > 0 ? t("labels.no_data_filtered", { total }) : t("labels.no_data")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="w-100 d-flex align-items-center justify-content-end flex-wrap mt-2">
              <span className="mb-3 me-3 text-secondary">
                {pagination
                  ? t("labels.pagination_showing", {
                      from: pagination.start_index + 1,
                      to: pagination.end_index,
                      total: pagination.count,
                    })
                  : ""}
              </span>
              <Pagination
                className="mb-3"
                count={Math.ceil((pagination?.count || 0) / PAGE_SIZE) || 1}
                page={this.state.page}
                showFirstButton
                showLastButton
                onChange={(_, page) => this.Get(page)}
              />
            </div>
          </div>
        </div>

        {/* ---------- MODAL ยืนยันยกเลิก Label ---------- */}
        <Modal show={!!voidTarget} onHide={this.closeVoidModal} centered>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: 18 }}>{t("labels.void_title")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-3">
              {t("labels.void_text", { label: voidTarget?.label_no || "-" })}
            </p>

            <div className="mb-2 text-12 text-secondary">
              {t("labels.col_lot_no")}: {voidTarget?.batch_no || "-"}
              {" · "}
              {t("labels.col_product")}: {voidTarget?.product?.name || "-"}
            </div>

            <div className="text-12 text-secondary mb-1">
              {t("labels.void_reason_label")}
            </div>
            <textarea
              className="form-control"
              rows={3}
              placeholder={t("labels.void_reason_placeholder")}
              value={voidReason}
              onChange={(e) => this.setState({ voidReason: e.target.value, voidError: "" })}
            />
            {voidError && <div className="text-danger text-12 mt-2">{voidError}</div>}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-secondary" onClick={this.closeVoidModal} disabled={isVoiding}>
              {t("labels.void_cancel")}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={this.confirmVoid}
              disabled={isVoiding || voidReason.trim().length < MIN_REASON_LENGTH}
            >
              {t("labels.void_confirm")}
            </button>
          </Modal.Footer>
        </Modal>

        {/* ---------- MODAL นำเข้าข้อมูลยกยอด ---------- */}
        <Modal show={importOpen} onHide={this.closeImportModal} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: 18 }}>{t("labels.import_title")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-12 text-secondary mb-2">{t("labels.import_hint")}</p>
            <input
              type="file"
              className="form-control mb-3"
              accept=".xlsx"
              disabled={isImporting}
              onChange={(e) => this.setState({ importFile: e.target.files?.[0] || null, importResult: null, importError: "" })}
            />

            {isImporting && importJob && (
              <div className="mb-3">
                <div className="d-flex align-items-center mb-1" style={{ gap: 8 }}>
                  <span className="spinner-border spinner-border-sm text-primary" role="status" />
                  <span className="fw-bold">
                    {t(`labels.import_stage_${importJob.stage}`)}
                    {importJob.total > 1 &&
                      ` ${Number(importJob.done).toLocaleString()} / ${Number(importJob.total).toLocaleString()}`}
                  </span>
                  <span className="ms-auto text-12 text-secondary">
                    {t("labels.import_elapsed", { seconds: importElapsed })}
                  </span>
                </div>
                <div className="progress" style={{ height: 10 }}>
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    style={{ width: `${this.importProgress(importJob, importDryRun)}%` }}
                  />
                </div>
                <div className="text-12 text-secondary mt-1">{t("labels.import_working_hint")}</div>
              </div>
            )}

            {importError && <div className="text-danger mb-2">{importError}</div>}

            {importResult && (
              <div>
                <div className={`mb-2 fw-bold ${importResult.valid ? "text-success" : "text-danger"}`}>
                  {importResult.valid
                    ? t("labels.import_valid")
                    : t("labels.import_invalid", { count: importResult.errors.length })}
                </div>
                <div className="text-12 mb-2">
                  {t("labels.import_summary", {
                    lots: importResult.summary.lots,
                    pallets: importResult.summary.pallets,
                    labels: importResult.summary.labels,
                    amount: toFixed(importResult.summary.pallet_amount, 2),
                  })}
                  {" · "}
                  {Object.entries(importResult.summary.by_status || {})
                    .map(([k, v]) => `${k} ${v}`)
                    .join(", ")}
                </div>
                {importIssues.length > 0 && (
                  <div style={{ maxHeight: 280, overflowY: "auto" }}>
                    <table className="table table-sm text-12 mb-0">
                      <thead>
                        <tr>
                          <th>{t("labels.import_col_sheet")}</th>
                          <th>{t("labels.import_col_row")}</th>
                          <th>{t("labels.import_col_message")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importIssues.slice(0, MAX_IMPORT_ISSUES_SHOWN).map((e, i) => (
                          <tr key={i} className={e.level === "error" ? "text-danger" : "text-warning"}>
                            <td>{e.sheet}</td>
                            <td>{e.row}</td>
                            <td>{e.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {importIssues.length > MAX_IMPORT_ISSUES_SHOWN && (
                      <div className="text-12 text-secondary mt-1">
                        {t("labels.import_more_issues", { shown: MAX_IMPORT_ISSUES_SHOWN, total: importIssues.length })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-secondary" onClick={this.closeImportModal} disabled={isImporting}>
              {t("labels.void_cancel")}
            </button>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => this.submitImport(true)}
              disabled={isImporting || !importFile}
            >
              {t("labels.import_check")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => this.submitImport(false)}
              disabled={isImporting || !importFile || !importResult?.valid}
            >
              {t("labels.import_confirm")}
            </button>
          </Modal.Footer>
        </Modal>

        <ScrollToTopButton />
      </div>
    );
  }
}

export default withTranslation()(LabelManage);
